import React from 'react';

/**
 * The flag beside a LANGUAGE name (the user menu's «Idioma», the Admin's
 * support cases), drawn as inline SVG.
 *
 * Not an emoji on purpose: Windows has no flag emoji and draws the regional
 * indicators as two letters, so the picker read «ES Español», «GB English».
 * These are local, simplified flags — no request leaves the page, and at
 * 20 px a coat of arms is noise (Spain and Portugal keep a hint of theirs).
 *
 * Keyed by LANGUAGE code, like the picker: English shows the United Kingdom
 * and Portuguese Portugal (we ship pt-PT), Czech the Czech Republic. A flag
 * never equals a language, so the native name always goes beside it; that is
 * also why it is `aria-hidden`.
 *
 * Country flags for every country (phone prefixes, Admin → Países) are a
 * different thing and still use `flagEmoji` from `lib/countries`.
 */

type Flag = { viewBox: string; body: React.ReactNode };

const h3 = (a: string, b: string, c: string): React.ReactNode => (
    <>
        <rect width="30" height="20" fill={b} />
        <rect width="30" height="6.67" fill={a} />
        <rect y="13.33" width="30" height="6.67" fill={c} />
    </>
);
const v3 = (a: string, b: string, c: string): React.ReactNode => (
    <>
        <rect width="30" height="20" fill={b} />
        <rect width="10" height="20" fill={a} />
        <rect x="20" width="10" height="20" fill={c} />
    </>
);

const FLAGS: Record<string, Flag> = {
    es: {
        viewBox: '0 0 30 20',
        body: (
            <>
                <rect width="30" height="20" fill="#AA151B" />
                <rect y="5" width="30" height="10" fill="#F1BF00" />
                <rect x="7" y="7.5" width="3.2" height="5" rx="0.6" fill="#AA151B" />
            </>
        ),
    },
    en: {
        viewBox: '0 0 60 40',
        body: (
            <>
                <rect width="60" height="40" fill="#012169" />
                <path d="M0 0L60 40M60 0L0 40" stroke="#FFFFFF" strokeWidth="8" />
                <path d="M0 0L60 40M60 0L0 40" stroke="#C8102E" strokeWidth="2.6" />
                <path d="M30 0V40M0 20H60" stroke="#FFFFFF" strokeWidth="12" />
                <path d="M30 0V40M0 20H60" stroke="#C8102E" strokeWidth="7" />
            </>
        ),
    },
    fr: { viewBox: '0 0 30 20', body: v3('#002654', '#FFFFFF', '#CE1126') },
    it: { viewBox: '0 0 30 20', body: v3('#009246', '#FFFFFF', '#CE2B37') },
    de: { viewBox: '0 0 30 20', body: h3('#000000', '#DD0000', '#FFCE00') },
    pt: {
        viewBox: '0 0 30 20',
        body: (
            <>
                <rect width="30" height="20" fill="#FF0000" />
                <rect width="12" height="20" fill="#006600" />
                <circle cx="12" cy="10" r="3.8" fill="none" stroke="#FFCC00" strokeWidth="1.3" />
                <rect x="10.7" y="8.3" width="2.6" height="3.2" rx="0.5" fill="#FFFFFF" stroke="#FF0000" strokeWidth="0.8" />
            </>
        ),
    },
    pl: {
        viewBox: '0 0 30 20',
        body: (
            <>
                <rect width="30" height="20" fill="#FFFFFF" />
                <rect y="10" width="30" height="10" fill="#DC143C" />
            </>
        ),
    },
    nl: { viewBox: '0 0 30 20', body: h3('#AE1C28', '#FFFFFF', '#21468B') },
    cs: {
        viewBox: '0 0 30 20',
        body: (
            <>
                <rect width="30" height="20" fill="#FFFFFF" />
                <rect y="10" width="30" height="10" fill="#D7141A" />
                <path d="M0 0L15 10L0 20Z" fill="#11457E" />
            </>
        ),
    },
};

/** Whether `lang` has a flag here (the nine languages the tenant ships). */
export const hasLanguageFlag = (lang: string | null | undefined): boolean => !!lang && lang in FLAGS;

export interface LanguageFlagProps {
    /** Language code: es, en, fr, it, de, pt, pl, nl, cs. Unknown → nothing. */
    readonly lang: string | null | undefined;
    /** Size and spacing; defaults to 20 × 14 px. */
    readonly className?: string;
}

const LanguageFlag: React.FC<LanguageFlagProps> = ({ lang, className = 'h-3.5 w-5' }) => {
    const flag = lang ? FLAGS[lang] : undefined;
    if (!flag) return null;
    return (
        <svg
            viewBox={flag.viewBox}
            preserveAspectRatio="none"
            aria-hidden="true"
            focusable="false"
            // The ring keeps a white stripe (Poland, Czechia, France…) visible
            // on a white menu and on a dark one.
            className={`inline-block shrink-0 overflow-hidden rounded-[2px] ring-1 ring-black/10 dark:ring-white/20 ${className}`}
        >
            {flag.body}
        </svg>
    );
};

export default LanguageFlag;
